import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Eye, EyeOff, Save, Clock, RefreshCw, Mail, Key, Database, Download, Loader2, CalendarRange } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useSettingsStore } from '../stores/settingsStore';
import {
  getAuth,
  sendPasswordResetEmail
} from 'firebase/auth';

export const SettingsPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [currentVersion, setCurrentVersion] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [isUpdatingVersion, setIsUpdatingVersion] = useState(false);
  const [lastUpdateInfo, setLastUpdateInfo] = useState<{
    timestamp: number;
    updatedBy: string;
  } | null>(null);

  // Backup state
  const [isBackupLoading, setIsBackupLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState({ current: 0, total: 0 });

  // Master password state
  const [masterPassword, setMasterPassword] = useState('');
  const [newMasterPassword, setNewMasterPassword] = useState('');
  const [isUpdatingMasterPassword, setIsUpdatingMasterPassword] = useState(false);
  const [masterPasswordLastUpdate, setMasterPasswordLastUpdate] = useState<{
    updatedAt: number;
    updatedBy: string;
  } | null>(null);

  // Organizer access window (Hajj period)
  const [organizerAccessStart, setOrganizerAccessStart] = useState('');
  const [organizerAccessEnd, setOrganizerAccessEnd] = useState('');
  const [isUpdatingOrganizerAccess, setIsUpdatingOrganizerAccess] = useState(false);
  const [organizerAccessLastUpdate, setOrganizerAccessLastUpdate] = useState<{
    updatedAt: number;
    updatedBy: string;
  } | null>(null);

  const { 
    fetchMasterPassword, 
    updateMasterPassword, 
    fetchOrganizerAccessWindow,
    updateOrganizerAccessWindow,
    loading: passwordLoading 
  } = useSettingsStore();

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  const { translations, direction } = useLanguage();
  const isRTL = direction === 'rtl';
  const isAdmin = userRole === 'admin';

  const toDateTimeLocal = (timestamp: number | null | undefined) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const parseDateTimeLocal = (value: string) => {
    if (!value) return null;
    return new Date(value).getTime();
  };

  const getCollectionName = () => {
    switch (userRole) {
      case 'guide':
        return 'Guides';
      case 'organizer':
        return 'Organizers';
      default:
        return 'Users';
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const collectionName = getCollectionName();
        const userRef = doc(firestore, collectionName, userId!);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserEmail(userData.email || '');
        }

        // Fetch version info if admin
        if (isAdmin) {
          const versionRef = doc(firestore, 'version', 'version');
          const versionDoc = await getDoc(versionRef);
          if (versionDoc.exists()) {
            const versionData = versionDoc.data();
            setCurrentVersion(versionData.version);
            setNewVersion(versionData.version);
            setLastUpdateInfo({
              timestamp: versionData.updatedAt || Date.now(),
              updatedBy: versionData.updatedBy || 'Unknown'
            });
          }

          // Fetch master password
          try {
            const password = await fetchMasterPassword();
            setMasterPassword(password);
            
            // Get last update info
            const passwordDocRef = doc(firestore, 'MasterPassword', 'password');
            const passwordDoc = await getDoc(passwordDocRef);
            if (passwordDoc.exists() && passwordDoc.data().updatedAt) {
              setMasterPasswordLastUpdate({
                updatedAt: passwordDoc.data().updatedAt,
                updatedBy: passwordDoc.data().updatedBy || 'Unknown'
              });
            }
          } catch (error) {
            console.error('Error fetching master password:', error);
            toast.error('Failed to fetch master password');
          }

          // Fetch organizer access window (Hajj period)
          try {
            const accessWindow = await fetchOrganizerAccessWindow();
            setOrganizerAccessStart(toDateTimeLocal(accessWindow.startAt));
            setOrganizerAccessEnd(toDateTimeLocal(accessWindow.endAt));
            if (accessWindow.updatedAt) {
              setOrganizerAccessLastUpdate({
                updatedAt: accessWindow.updatedAt,
                updatedBy: accessWindow.updatedBy || 'Unknown'
              });
            }
          } catch (error) {
            console.error('Error fetching organizer access window:', error);
            toast.error('Failed to fetch organizer access window');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(
          translations?.settingsPage?.fetchError || 'Failed to fetch data'
        );
      } finally {
        setIsFetching(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId, userRole, translations, isAdmin, fetchMasterPassword, fetchOrganizerAccessWindow]);

  const handleSendPasswordResetEmail = async () => {
    if (!userEmail) {
      toast.error(
        translations?.settingsPage?.emailRequired ||
          'Email address is required'
      );
      return;
    }

    setIsLoading(true);
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, userEmail);

      toast.success(
        translations?.settingsPage?.resetEmailSent ||
          'Password reset email has been sent. Please check your inbox.'
      );
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      let errorMessage =
        translations?.settingsPage?.resetEmailError || 'Failed to send password reset email';

      if (error.code === 'auth/user-not-found') {
        errorMessage =
          translations?.settingsPage?.userNotFound ||
          'No user found with this email address';
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateVersion = async () => {
    if (!newVersion.trim()) {
      toast.error('Please enter a new version number');
      return;
    }

    if (newVersion === currentVersion) {
      toast.error('New version must be different from current version');
      return;
    }

    setIsUpdatingVersion(true);
    try {
      const versionRef = doc(firestore, 'version', 'version');
      await updateDoc(versionRef, {
        version: newVersion,
        updatedAt: Date.now(),
        updatedBy: userName || 'Unknown'
      });

      setCurrentVersion(newVersion);
      setLastUpdateInfo({
        timestamp: Date.now(),
        updatedBy: userName || 'Unknown'
      });

      toast.success('Version updated successfully');
    } catch (error) {
      console.error('Error updating version:', error);
      toast.error('Failed to update version');
    } finally {
      setIsUpdatingVersion(false);
    }
  };

  const handleUpdateMasterPassword = async () => {
    if (!newMasterPassword) {
      toast.error('Please enter a new master password');
      return;
    }

    if (newMasterPassword === masterPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setIsUpdatingMasterPassword(true);
    try {
      await updateMasterPassword(newMasterPassword);
      setMasterPassword(newMasterPassword);
      setNewMasterPassword('');
      setMasterPasswordLastUpdate({
        updatedAt: Date.now(),
        updatedBy: userName || 'Unknown'
      });
      toast.success('Master password updated successfully');
    } catch (error: any) {
      console.error('Error updating master password:', error);
      toast.error(error.message || 'Failed to update master password');
    } finally {
      setIsUpdatingMasterPassword(false);
    }
  };

  const handleUpdateOrganizerAccessWindow = async () => {
    const startAt = parseDateTimeLocal(organizerAccessStart);
    const endAt = parseDateTimeLocal(organizerAccessEnd);

    if ((startAt && !endAt) || (!startAt && endAt)) {
      toast.error('Please select both start and end times');
      return;
    }

    setIsUpdatingOrganizerAccess(true);
    try {
      await updateOrganizerAccessWindow(startAt, endAt);
      setOrganizerAccessLastUpdate({
        updatedAt: Date.now(),
        updatedBy: userName || 'Unknown'
      });
      toast.success('Organizer access window updated');
    } catch (error: any) {
      console.error('Error updating organizer access window:', error);
      toast.error(error.message || 'Failed to update organizer access window');
    } finally {
      setIsUpdatingOrganizerAccess(false);
    }
  };

  const handleBackupDownload = async () => {
    if (!isAdmin) {
      toast.error('Only administrators can download backups');
      return;
    }
    
    setIsBackupLoading(true);
    setBackupProgress({ current: 0, total: 0 });
    
    try {
      // Define collections to backup
      const collections = [
        'AccommodationTables',
        'Bans',
        'CampAccommodationTables',
        'Disputes',
        'Flights',
        'Guides',
        'GuideBans',
        'Hotels',
        'MasterPassword',
        'Organizers',
        'Packages',
        'PilgrimGuideLinks',
        'PilgrimOwnership',
        'PilgrimSelector',
        'Pilgrims',
        'Roles',
        'Settings',
        'Suspensions',
        'UpgradeRequests',
        'Users',
        'verifications'
      ];
      
      setBackupProgress({ current: 0, total: collections.length });
      const backupData: Record<string, any> = {};
      
      // Fetch data from all collections
      for (let i = 0; i < collections.length; i++) {
        const collectionName = collections[i];
        try {
          const querySnapshot = await getDocs(collection(firestore, collectionName));
          
          const collectionData: Record<string, any> = {};
          querySnapshot.docs.forEach(doc => {
            collectionData[doc.id] = doc.data();
          });
          
          backupData[collectionName] = collectionData;
          setBackupProgress({ current: i + 1, total: collections.length });
        } catch (error) {
          console.error(`Error fetching collection ${collectionName}:`, error);
        }
      }
      
      // Convert data to JSON string
      const jsonData = JSON.stringify(backupData, null, 2);
      
      // Create a Blob from the JSON string
      const blob = new Blob([jsonData], { type: 'application/json' });
      
      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);
      
      // Create a link element and trigger a download
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.download = `ithraa-backup-${date}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success('Backup downloaded successfully');
    } catch (error: any) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup: ' + (error.message || 'Unknown error'));
    } finally {
      setIsBackupLoading(false);
    }
  };

  if (isFetching || passwordLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
      >
        <div className="p-2 bg-primary-50 rounded-lg">
          <Settings className="text-primary-500" size={24} />
        </div>
        <div>
          <h1 className={`text-2xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
            {translations?.settingsPage?.headerTitle || 'Settings'}
          </h1>
          <p className={`text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {translations?.settingsPage?.headerSubtitle ||
              'Manage account settings'}
          </p>
        </div>
      </motion.div>

      {/* Backup Download Section - Admin Only */}
      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold mb-4">
            Database Backup
          </h2>
          
          <div className="max-w-md space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-3 text-blue-700">
                <Database size={20} className="shrink-0 mt-1" />
                <div>
                  <p className="font-medium">Database Backup Information</p>
                  <p className="mt-1 text-sm">
                    Download a complete backup of all database collections in JSON format. This file contains sensitive data and should be stored securely.
                  </p>
                </div>
              </div>
            </div>
            
            {isBackupLoading && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Loader2 size={20} className="text-primary-500 animate-spin" />
                  <p className="font-medium">Creating backup...</p>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-primary-500 h-2.5 rounded-full transition-all"
                    style={{ width: `${Math.round((backupProgress.current / backupProgress.total) * 100)}%` }}
                  ></div>
                </div>
                
                <div className="mt-2 text-sm text-gray-500">
                  {backupProgress.current} of {backupProgress.total} collections processed
                </div>
              </div>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBackupDownload}
              disabled={isBackupLoading}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg text-white
                ${isBackupLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'}`}
            >
              {isBackupLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Creating Backup...</span>
                </>
              ) : (
                <>
                  <Download size={20} />
                  <span>Download Database Backup</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Master Password Management Section - Admin Only */}
      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className={`text-lg font-semibold mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            {translations?.settingsPage?.masterPasswordTitle || 'Master Password'}
          </h2>

          <div className="space-y-6 max-w-md">
            {/* Current Master Password Display */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {translations?.settingsPage?.currentMasterPassword || 'Current Master Password'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={masterPassword}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg ${isRTL ? 'text-right' : 'text-left'} bg-gray-50`}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                
                {masterPasswordLastUpdate && (
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Clock size={12} />
                    <span>
                      Last updated by {masterPasswordLastUpdate.updatedBy} on{' '}
                      {new Date(masterPasswordLastUpdate.updatedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* New Master Password Input */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {translations?.settingsPage?.newMasterPassword || 'New Master Password'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newMasterPassword}
                  onChange={(e) => setNewMasterPassword(e.target.value)}
                  placeholder={translations?.settingsPage?.newMasterPasswordPlaceholder || 'Enter new master password'}
                  className={`w-full px-4 py-2 border rounded-lg ${isRTL ? 'text-right' : 'text-left'} focus:ring-2 focus:ring-primary-500`}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Update Button */}
            <motion.button
              onClick={handleUpdateMasterPassword}
              disabled={isUpdatingMasterPassword || !newMasterPassword.trim() || newMasterPassword === masterPassword}
              className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-white
                ${isUpdatingMasterPassword || !newMasterPassword.trim() || newMasterPassword === masterPassword
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-primary-500 hover:bg-primary-600'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isUpdatingMasterPassword ? (
                <>
                  <RefreshCw size={20} className="animate-spin" />
                  <span>{translations?.settingsPage?.updating || 'Updating...'}</span>
                </>
              ) : (
                <>
                  <Key size={20} />
                  <span>{translations?.settingsPage?.updateMasterPassword || 'Update Master Password'}</span>
                </>
              )}
            </motion.button>

            {/* Help Text */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-2 text-blue-700">
                <div className="shrink-0 mt-1">
                  <Key size={16} />
                </div>
                <div className="text-sm">
                  <p className="font-medium">
                    {translations?.settingsPage?.masterPasswordInfo || 'Master Password Information'}
                  </p>
                  <p className="mt-1">
                    {translations?.settingsPage?.masterPasswordDescription || 
                      'The master password allows administrators to log in as any organizer or guide. This is useful for support and troubleshooting purposes. Keep this password secure and change it regularly.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Organizer Access Window (Hajj Period) - Admin Only */}
      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className={`text-lg font-semibold mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            Organizer Access Window (Hajj Period)
          </h2>

          <div className="space-y-6 max-w-md">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={organizerAccessStart}
                  onChange={(e) => setOrganizerAccessStart(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${isRTL ? 'text-right' : 'text-left'} focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={organizerAccessEnd}
                  onChange={(e) => setOrganizerAccessEnd(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${isRTL ? 'text-right' : 'text-left'} focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              {organizerAccessLastUpdate && (
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock size={12} />
                  <span>
                    Last updated by {organizerAccessLastUpdate.updatedBy} on{' '}
                    {new Date(organizerAccessLastUpdate.updatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <motion.button
              onClick={handleUpdateOrganizerAccessWindow}
              disabled={isUpdatingOrganizerAccess}
              className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-white
                ${isUpdatingOrganizerAccess
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isUpdatingOrganizerAccess ? (
                <>
                  <RefreshCw size={20} className="animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <CalendarRange size={20} />
                  <span>Update Access Window</span>
                </>
              )}
            </motion.button>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-2 text-blue-700">
                <div className="shrink-0 mt-1">
                  <CalendarRange size={16} />
                </div>
                <div className="text-sm">
                  <p className="font-medium">Organizer Access Policy</p>
                  <p className="mt-1">
                    Organizers can sign in only within this window. Outside the range, organizers will be logged out and denied access.
                    Leave both fields empty to disable the restriction.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Version Management Section - Admin Only */}
      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className={`text-lg font-semibold mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            Version Management
          </h2>

          <div className="space-y-6 max-w-md">
            {/* Current Version Display */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                Current Version
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1 px-4 py-2 bg-gray-50 rounded-lg">
                  <div className="font-medium">v{currentVersion}</div>
                  {lastUpdateInfo && (
                    <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                      <Clock size={14} />
                      <span>
                        Last updated by {lastUpdateInfo.updatedBy} on{' '}
                        {new Date(lastUpdateInfo.timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* New Version Input */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                New Version
              </label>
              <div className="flex gap-4">
                <input
                  type="text"
                  value={newVersion}
                  onChange={(e) => setNewVersion(e.target.value)}
                  placeholder="Enter new version number"
                  className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isRTL ? 'text-right' : 'text-left'}`}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleUpdateVersion}
                  disabled={isUpdatingVersion || !newVersion.trim() || newVersion === currentVersion}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-colors ${
                    isUpdatingVersion || !newVersion.trim() || newVersion === currentVersion
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-500 hover:bg-primary-600'
                  }`}
                >
                  {isUpdatingVersion ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>Update Version</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Version Update Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-2 text-blue-700">
                <div className="shrink-0 mt-1">
                  <Clock size={16} />
                </div>
                <div className="text-sm">
                  <p className="font-medium">Version Update Information</p>
                  <p className="mt-1">
                    Updating the version will trigger an automatic refresh for all users,
                    ensuring they are using the latest version of the application.
                    Make sure to update this version number whenever deploying new changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Password Management Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className={`text-lg font-semibold mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
          {translations?.settingsPage?.resetPasswordTitle || 'Reset Password'}
        </h2>

        <div className="space-y-6 max-w-md">
          {/* Email Display */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              {translations?.settingsPage?.emailLabel ||
                'Email Address'}
            </label>
            <input
              type="email"
              value={userEmail}
              readOnly
              className={`w-full px-4 py-2 border rounded-lg bg-gray-50 ${isRTL ? 'text-right' : 'text-left'}`}
            />
          </div>

          {/* Info Message */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start gap-2 text-blue-700">
              <div className="shrink-0 mt-1">
                <Mail size={16} />
              </div>
              <div className="text-sm">
                <p className="font-medium">
                  {translations?.settingsPage?.resetInfoTitle || 'Password Reset Information'}
                </p>
                <p className="mt-1">
                  {translations?.settingsPage?.resetInfoDescription || 
                    'To reset your password, click the button below. A password reset link will be sent to your email address. Follow the instructions in the email to create a new password.'}
                </p>
              </div>
            </div>
          </div>

          {/* Reset Button */}
          <motion.button
            onClick={handleSendPasswordResetEmail}
            disabled={isLoading || !userEmail}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white
              ${isLoading || !userEmail
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'} 
              transition-colors`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Mail size={20} />
            <span>
              {isLoading
                ? translations?.settingsPage?.sendingEmail || 'Sending email...'
                : translations?.settingsPage?.sendResetEmail || 'Send Password Reset Email'}
            </span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};
